import type {
  QuoteDetailPublic,
  QuoteTaskDetailPublic,
  QuoteTaskGroupPublic,
} from "@common/client"
import { z } from "zod"
import { type Group, type GroupLabel, groupRows } from "@/lib/grouping"
import { phaseGroupsOf } from "@/lib/phases"
import { sumAmounts } from "@/routes/_layout/quoting_/quotes/-lib/charge-math"
import { totalCharges } from "@/routes/_layout/quoting_/quotes/builder/$quoteId/-common/lib/task-form"
import { aircraftTypesSchema } from "./aircraft-types"
import { DUE_OUTLOOKS } from "./due"
import type { TaskFilterValues } from "./filters"
import { labourCharge, labourCharges, labourMethod } from "./labour"
import { highestOffer, isPartsQuote } from "./parts-quote"
import { tabSchema } from "./tabs"
import { lastUpdated, TASK_STATUSES, taskStatusLabel } from "./task-values"

export const GROUPINGS = [
  "phase",
  "task_group",
  "status",
  "billing",
  "ata",
] as const

export const groupingSchema = z.enum(GROUPINGS)

export type Grouping = z.infer<typeof groupingSchema>

export const GROUPING_LABELS: Record<Grouping, string> = {
  phase: "Phase",
  task_group: "Task group",
  status: "Status",
  billing: "Billing",
  ata: "ATA",
}

export const COLUMNS = [
  "ata",
  "task_group",
  "department",
  "billing",
  "hours",
  "labour_price",
  "parts",
  "parts_price",
  "services",
  "services_price",
  "total",
  "comments",
  "updated",
] as const

const columnSchema = z.enum(COLUMNS)

export type Column = z.infer<typeof columnSchema>

const DEFAULT_COLUMNS: Column[] = [
  "hours",
  "parts_price",
  "services_price",
  "total",
  "comments",
  "updated",
]

export const COLUMN_LABELS: Record<Column, string> = {
  ata: "ATA",
  task_group: "Task group",
  department: "Department",
  billing: "Labor billing",
  hours: "Hours",
  labour_price: "Labor price",
  parts: "Parts",
  parts_price: "Parts price",
  services: "Services",
  services_price: "Services price",
  total: "Total",
  comments: "Comments",
  updated: "Updated",
}

export const quoteSearchSchema = z.object({
  tab: tabSchema.optional().catch(undefined),
  group: z
    .enum([...GROUPINGS, "none"])
    .optional()
    .catch(undefined),
  subgroup: z
    .enum([...GROUPINGS, "none"])
    .optional()
    .catch(undefined),
  status: z.array(z.enum(TASK_STATUSES)).optional().catch(undefined),
  due: z.array(z.enum(DUE_OUTLOOKS)).optional().catch(undefined),
  sort: z
    .enum(["task", "description", "total", "updated"])
    .optional()
    .catch(undefined),
  dir: z.enum(["asc", "desc"]).optional().catch(undefined),
  columns: z.array(columnSchema).optional().catch(undefined),
  doc: z.coerce.number().int().positive().optional().catch(undefined),
  preview: z.boolean().optional().catch(undefined),
  section: z.literal("parts").optional().catch(undefined),
  aircraft_types: aircraftTypesSchema.optional().catch(undefined),
  return_to: z
    .string()
    .startsWith("/quoting/squawks")
    .optional()
    .catch(undefined),
})

export type ItemSort = NonNullable<z.infer<typeof quoteSearchSchema>["sort"]>

export type SortDirection = "asc" | "desc"

export type QuoteSearch = z.infer<typeof quoteSearchSchema>

export type ItemsDisplay = {
  group: Grouping | undefined
  subgroup: Grouping | undefined
}

const SUBGROUPS: Partial<Record<Grouping, Grouping>> = {
  phase: "task_group",
  task_group: "status",
}

export function groupingsOf(phasesEnabled: boolean): readonly Grouping[] {
  return phasesEnabled
    ? GROUPINGS
    : GROUPINGS.filter((grouping) => grouping !== "phase")
}

function defaultGroup(
  quote: QuoteDetailPublic,
  phasesEnabled: boolean,
): Grouping {
  return phasesEnabled && quote.active_phases.length > 0
    ? "phase"
    : "task_group"
}

function among(
  offered: readonly Grouping[],
  grouping: Grouping | "none" | undefined,
): Grouping | undefined {
  return grouping && grouping !== "none" && offered.includes(grouping)
    ? grouping
    : undefined
}

export function displayOf(
  search: QuoteSearch,
  quote: QuoteDetailPublic,
  phasesEnabled: boolean,
): ItemsDisplay {
  const offered = groupingsOf(phasesEnabled)
  const group =
    search.group === "none"
      ? undefined
      : (among(offered, search.group) ?? defaultGroup(quote, phasesEnabled))
  const subgroup = among(
    offered,
    search.subgroup ?? (group && SUBGROUPS[group]),
  )
  return { group, subgroup: group && subgroup !== group ? subgroup : undefined }
}

export function groupingSearch(
  { group, subgroup }: ItemsDisplay,
  quote: QuoteDetailPublic,
  phasesEnabled: boolean,
): Partial<QuoteSearch> {
  const natural = group && SUBGROUPS[group]
  return {
    group:
      group === defaultGroup(quote, phasesEnabled)
        ? undefined
        : (group ?? "none"),
    subgroup: subgroup === natural ? undefined : (subgroup ?? "none"),
  }
}

export function columnsOffered(
  quote: QuoteDetailPublic,
  readable: { groups: boolean; departments: boolean; comments: boolean },
): Column[] {
  return COLUMNS.filter((column) => {
    switch (column) {
      case "task_group":
        return quote.kind === "aircraft" && readable.groups
      case "department":
        return readable.departments
      case "comments":
        return readable.comments
      case "billing":
      case "hours":
      case "labour_price":
      case "parts":
      case "parts_price":
      case "services":
      case "services_price":
        return !isPartsQuote(quote)
      default:
        return true
    }
  })
}

export function columnsShown(search: QuoteSearch, offered: Column[]): Column[] {
  const columns = search.columns ?? DEFAULT_COLUMNS
  return offered.filter((column) => columns.includes(column))
}

export function columnsSearch(
  columns: Column[],
  offered: Column[],
): Pick<QuoteSearch, "columns"> {
  const defaults = offered.filter((column) => DEFAULT_COLUMNS.includes(column))
  const same =
    columns.length === defaults.length &&
    columns.every((column) => defaults.includes(column))
  return { columns: same ? undefined : columns }
}

const BILLING = ["component", "tm", "flat", "mixed", "none", "unset"] as const

type Billing = (typeof BILLING)[number]

export function billingOf(
  quote: QuoteDetailPublic,
  task: QuoteTaskDetailPublic,
): Billing {
  if (isPartsQuote(quote)) return "component"
  if (new Set(labourCharges(task).map(labourMethod)).size > 1) return "mixed"
  return labourMethod(labourCharge(task)) ?? "unset"
}

export function billingLabel(billing: Billing): string {
  switch (billing) {
    case "component":
      return "Component"
    case "tm":
      return "Time & materials"
    case "flat":
      return "Flat rate"
    case "mixed":
      return "Mixed"
    case "none":
      return "No labor charge"
    case "unset":
      return "Labor not set"
  }
}

function amountOf(
  quote: QuoteDetailPublic,
  task: QuoteTaskDetailPublic,
): number {
  const charges = isPartsQuote(quote)
    ? [highestOffer(task)].filter((charge) => charge !== null)
    : totalCharges(task)
  return sumAmounts(charges) ?? 0
}

function sortKey(
  quote: QuoteDetailPublic,
  task: QuoteTaskDetailPublic,
  sort: ItemSort,
): string | number {
  switch (sort) {
    case "task":
      return task.number
    case "description":
      return (task.description ?? "").toLowerCase()
    case "total":
      return amountOf(quote, task)
    case "updated":
      return lastUpdated(task)
  }
}

export function sorted(
  quote: QuoteDetailPublic,
  tasks: QuoteTaskDetailPublic[],
  sort: ItemSort | undefined,
  direction: SortDirection,
): QuoteTaskDetailPublic[] {
  if (!sort) return tasks
  const flip = direction === "desc" ? -1 : 1
  return [...tasks].sort((a, b) => {
    const left = sortKey(quote, a, sort)
    const right = sortKey(quote, b, sort)
    const order =
      typeof left === "string" && typeof right === "string"
        ? left.localeCompare(right, undefined, { numeric: true })
        : Number(left) - Number(right)
    return order * flip
  })
}

export function filtered(
  tasks: QuoteTaskDetailPublic[],
  { status, due }: TaskFilterValues,
): QuoteTaskDetailPublic[] {
  return tasks.filter(
    (task) =>
      (!status?.length || status.includes(task.status)) &&
      (!due?.length || (task.due_outlook && due.includes(task.due_outlook))),
  )
}

export function itemGroup(
  quote: QuoteDetailPublic,
  task: QuoteTaskDetailPublic,
  grouping: Grouping,
  taskGroups: QuoteTaskGroupPublic[],
): GroupLabel | GroupLabel[] {
  switch (grouping) {
    case "phase":
      return phaseGroupsOf(quote.active_phases, task.phase_ids)
    case "task_group": {
      if (quote.kind !== "aircraft") {
        return { key: "component", label: "Component", order: 0 }
      }
      const taskGroup = taskGroups.find(
        (candidate) => candidate.id === task.task_group_id,
      )
      return taskGroup
        ? {
            key: taskGroup.id,
            label: taskGroup.name,
            order: taskGroup.position,
          }
        : { key: "", label: "No task group", order: Number.MAX_SAFE_INTEGER }
    }
    case "status":
      return {
        key: task.status,
        label: taskStatusLabel(task.status, quote.disposition),
        order: TASK_STATUSES.indexOf(task.status),
      }
    case "billing": {
      const billing = billingOf(quote, task)
      return {
        key: billing,
        label: billingLabel(billing),
        order: BILLING.indexOf(billing),
      }
    }
    case "ata":
      return task.ata_chapter == null
        ? { key: "", label: "No ATA chapter", order: Number.MAX_SAFE_INTEGER }
        : {
            key: String(task.ata_chapter),
            label: `ATA ${task.ata_chapter}`,
            order: task.ata_chapter,
          }
  }
}

function grouped(
  quote: QuoteDetailPublic,
  tasks: QuoteTaskDetailPublic[],
  grouping: Grouping | undefined,
  taskGroups: QuoteTaskGroupPublic[],
): Group<QuoteTaskDetailPublic>[] {
  return grouping
    ? groupRows(tasks, (task) => itemGroup(quote, task, grouping, taskGroups))
    : [{ key: "", label: "", rows: tasks }]
}

export function inDisplayOrder(
  quote: QuoteDetailPublic,
  tasks: QuoteTaskDetailPublic[],
  display: ItemsDisplay,
  taskGroups: QuoteTaskGroupPublic[],
): QuoteTaskDetailPublic[] {
  const order = grouped(quote, tasks, display.group, taskGroups).flatMap(
    (group) =>
      grouped(quote, group.rows, display.subgroup, taskGroups).flatMap(
        (sub) => sub.rows,
      ),
  )
  return [...new Set(order)]
}
